/**
 * Daftar halaman marketing yang boleh diakses publik (tanpa login).
 *
 * Satu-satunya sumber kebenaran untuk tiga hal sekaligus:
 *   1. Penjaga 401 di `lib/api.ts` — menentukan siapa yang TIDAK dilempar ke /login.
 *   2. `app/sitemap.ts` — daftar URL yang dikirim ke Google.
 *   3. Menu "Solusi" di nav dan kolom Produk di footer.
 *
 * Menambah halaman marketing baru cukup dengan menambah satu baris di sini —
 * jangan menaruh daftar path publik di tempat lain lagi.
 */

export type MarketingRoute = {
  path: string;
  /** Label pendek untuk menu navigasi & footer. */
  judul: string;
  /** Satu baris penjelas, dipakai di dropdown menu. */
  ringkas: string;
  priority: number;
  /**
   * Tanggal isi halaman terakhir benar-benar berubah, format 'YYYY-MM-DD'.
   * Dipakai sebagai <lastmod> di sitemap — WAJIB diperbarui manual saat
   * teksnya diedit, dan sebaliknya JANGAN diisi tanggal build.
   *
   * Alasannya: kalau lastmod ikut berubah tiap deploy padahal isinya sama,
   * Google menganggap nilainya tidak kredibel dan berhenti mempercayai
   * lastmod di SELURUH sitemap ini — termasuk milik artikel blog yang
   * tanggalnya benar.
   */
  diperbarui: string;
};

/**
 * Tanggal isi landing page '/' terakhir diubah. Sama aturannya dengan
 * `diperbarui` di atas: naikkan manual, bukan otomatis dari waktu build.
 */
export const BERANDA_DIPERBARUI = '2026-07-22';

export const MARKETING_ROUTES: MarketingRoute[] = [
  {
    path: '/aplikasi-bengkel-mobil',
    judul: 'Bengkel Mobil',
    ringkas: 'Estimasi biaya, riwayat kendaraan, pekerjaan berhari-hari',
    priority: 0.9,
    diperbarui: '2026-07-22',
  },
  {
    path: '/aplikasi-bengkel-motor',
    judul: 'Bengkel Motor',
    ringkas: 'Antrian padat, servis cepat, sparepart laris',
    priority: 0.9,
    diperbarui: '2026-07-22',
  },
  {
    path: '/antrian-online-bengkel',
    judul: 'Antrian Online',
    ringkas: 'Booking, check-in QR, layar antrian di ruang tunggu',
    priority: 0.9,
    diperbarui: '2026-07-22',
  },
  {
    path: '/aplikasi-kasir-bengkel',
    judul: 'Kasir Bengkel',
    ringkas: 'Nota jasa + sparepart, promo, pajak, tutup kasir',
    priority: 0.8,
    diperbarui: '2026-07-22',
  },
  {
    path: '/laporan-keuangan-bengkel',
    judul: 'Laporan & Arus Kas',
    ringkas: 'Kas dan bank tercatat sendiri dari tiap transaksi',
    priority: 0.8,
    diperbarui: '2026-07-22',
  },
  {
    path: '/aplikasi-bengkel-multi-cabang',
    judul: 'Multi Cabang',
    ringkas: 'Data terpisah per cabang, laporan tetap menyatu',
    priority: 0.8,
    diperbarui: '2026-07-22',
  },
];

/**
 * Halaman legal & bantuan. Sengaja dipisah dari MARKETING_ROUTES karena array
 * itu mengisi dropdown "Solusi" yang berisi produk — halaman legal tidak
 * berada di sana, tapi tetap harus publik dan tetap masuk sitemap.
 *
 * Kelima halaman ini adalah syarat verifikasi payment gateway (iPaymu):
 * FAQ, Refund Policy, Syarat & Ketentuan, dan Kontak wajib berupa URL publik
 * yang bisa dibuka tanpa login. Jangan hapus salah satunya tanpa mengecek
 * ulang pendaftaran merchant.
 */
export const LEGAL_ROUTES: MarketingRoute[] = [
  {
    path: '/faq',
    judul: 'FAQ',
    ringkas: 'Pertanyaan yang sering ditanyakan seputar Garasin',
    priority: 0.6,
    diperbarui: '2026-07-22',
  },
  {
    path: '/kontak',
    judul: 'Kontak',
    ringkas: 'Alamat, telepon, dan email resmi kami',
    priority: 0.6,
    diperbarui: '2026-07-22',
  },
  {
    path: '/syarat-ketentuan',
    judul: 'Syarat & Ketentuan',
    ringkas: 'Ketentuan penggunaan layanan Garasin',
    priority: 0.4,
    diperbarui: '2026-07-22',
  },
  {
    path: '/kebijakan-refund',
    judul: 'Kebijakan Refund',
    ringkas: 'Aturan pengembalian dana tagihan platform',
    priority: 0.4,
    diperbarui: '2026-07-22',
  },
  {
    path: '/kebijakan-privasi',
    judul: 'Kebijakan Privasi',
    ringkas: 'Cara kami mengumpulkan dan menjaga data Anda',
    priority: 0.4,
    diperbarui: '2026-07-22',
  },
];

/** Prefix halaman auth yang juga boleh diakses tanpa sesi login. */
const PUBLIC_PREFIXES = ['/login', '/reset-password', '/verify-email', '/blog', '/sorry'];

/**
 * Apakah path ini boleh dibaca pengunjung anonim?
 *
 * Landing page '/' dicocokkan PERSIS — memakai `startsWith('/')` akan membuat
 * seluruh aplikasi dianggap publik.
 */
export function isPublicPath(path: string): boolean {
  if (path === '/') return true;
  if (MARKETING_ROUTES.some((r) => r.path === path)) return true;
  if (LEGAL_ROUTES.some((r) => r.path === path)) return true;
  return PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}
